import type { ExtensionDocumentation as DocumentationContract } from "@api-client";
import { DocumentationMarkdown } from "@sylviq/ui-react-web/components/docs/DocumentationMarkdown";

export type ExtensionDisplayMetadata = { summary?: string; documentation?: DocumentationContract };
const boundedText = (value: unknown, limit: number): value is string =>
  typeof value === "string" && value.trim().length > 0 && new TextEncoder().encode(value).length <= limit && !value.includes("\0");

/** Manifests from drafts and old releases are untrusted until validated. */
export function extensionDisplayMetadata(value: unknown): ExtensionDisplayMetadata {
  if (!value || typeof value !== "object") return {};
  const metadata = value as Record<string, unknown>;
  const result: ExtensionDisplayMetadata = {};
  if (boundedText(metadata.summary, 400)) result.summary = metadata.summary;
  if (metadata.documentation && typeof metadata.documentation === "object") {
    const docs = metadata.documentation as Record<string, unknown>;
    const documentation: DocumentationContract = {};
    if (boundedText(docs.usage, 6000)) documentation.usage = docs.usage;
    if (boundedText(docs.extending, 6000)) documentation.extending = docs.extending;
    if (documentation.usage || documentation.extending) result.documentation = documentation;
  }
  return result;
}

const allowedDocumentationHref = (href: string) =>
  !/[\\\u0000-\u0020]/.test(href) && (href.startsWith("#") || /^\/docs(?:\/|$)/.test(href));

export function ExtensionDocumentation({ publisher, extensionKey, metadata }: {
  publisher: string; extensionKey: string; metadata: unknown;
}) {
  const display = extensionDisplayMetadata(metadata);
  return <section aria-label="Extension documentation" className="space-y-4">
    {display.summary ? <p className="text-sm">{display.summary}</p> : null}
    <div className="space-y-2"><h3 className="font-semibold">Usage guide</h3>
      {display.documentation?.usage ? <DocumentationMarkdown content={display.documentation.usage} isAllowedHref={allowedDocumentationHref} /> : <p className="text-sm text-[var(--muted-foreground)]">No usage guide is included in this release. Consult its publisher before relying on undocumented behavior.</p>}
    </div>
    <div className="space-y-2"><h3 className="font-semibold">Extending this extension</h3>
      {display.documentation?.extending ? <DocumentationMarkdown content={display.documentation.extending} isAllowedHref={allowedDocumentationHref} /> : <p className="text-sm text-[var(--muted-foreground)]">No integration guide is included in this release. Dependencies and requested permissions must be declared explicitly; installation does not grant access to another extension’s data automatically.</p>}
    </div>
    <div className="space-y-2 text-sm"><h3 className="font-semibold">SYLVIQ host guides</h3>
      <p className="text-[var(--muted-foreground)]">Platform guidance is separate from this publisher’s immutable release documentation.</p>
      <ul className="space-y-2">
        <li><a className="underline" href="https://docs.sylviq.io/docs/console-extensions">Install, configure and manage extensions</a></li>
        <li><a className="underline" href="https://docs.sylviq.io/docs/console-extension-authoring">Create and extend an extension</a></li>
        {publisher === "sylviq" && ["metrc-traceability", "niche-cultivation-costing"].includes(extensionKey) ? <li><a className="underline" href="https://docs.sylviq.io/docs/console-metrc-extensions">METRC and cultivation costing in Accounting</a></li> : null}
      </ul>
    </div>
  </section>;
}
